import React, { useState, useEffect } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [destDropdownOpen, setDestDropdownOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    setMobileOpen(false)
    setDestDropdownOpen(false)
  }, [location])

  const destinationsList = [
    { name: 'St. Anton am Arlberg', slug: 'st-anton', land: 'Østrig' },
    { name: 'Saalbach Hinterglemm', slug: 'saalbach', land: 'Østrig' },
    { name: 'Val Gardena / Dolomitterne', slug: 'val-gardena', land: 'Italien' },
    { name: 'Val Thorens', slug: 'val-thorens', land: 'Frankrig' },
    { name: 'Hemsedal', slug: 'hemsedal', land: 'Norge' },
  ]

  return (
    <header className="sticky top-0 z-50 bg-slate-950/95 backdrop-blur-md border-b border-slate-800 text-slate-100">
      {/* Top Utility Bar */}
      <div className="bg-slate-900 border-b border-slate-800/80 px-4 py-2 text-xs sm:text-sm">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-2 sm:gap-3 text-center sm:text-left">
          <div className="flex items-center justify-center gap-2 text-slate-300">
            <i className="fa-solid fa-mountain-snow text-cyan-400" aria-hidden="true" />
            <span>Specialister i skirejser & alpeoplevelser</span>
          </div>

          <div className="flex items-center justify-center gap-4 sm:gap-6 font-medium whitespace-nowrap">
            <a
              href="tel:+4551589452"
              className="inline-flex items-center gap-1.5 text-slate-200 hover:text-cyan-400 transition-colors"
            >
              <i className="fa-solid fa-phone text-cyan-400" aria-hidden="true" />
              <span>+45 51 58 94 52</span>
            </a>
            <a
              href="mailto:claus@skiogco.dk"
              className="inline-flex items-center gap-1.5 text-slate-200 hover:text-cyan-400 transition-colors"
            >
              <i className="fa-solid fa-envelope text-cyan-400" aria-hidden="true" />
              <span>claus@skiogco.dk</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Header Nav */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group py-1">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/9bff8fd4-e9da-49ba-96b9-18c20ed7b396/ski-logo.jpeg"
            alt="SKI & CO TRAVEL ApS Logo"
            className="h-16 sm:h-20 w-auto object-contain"
          />
        </Link>

        {/* Desktop Navigation (>= 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center gap-1">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-cyan-500/10 text-cyan-400 font-semibold'
                  : 'text-slate-300 hover:text-white hover:bg-slate-800/60'
              }`
            }
          >
            Forside
          </NavLink>

          <NavLink
            to="/om-os"
            className={({ isActive }) =>
              `px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-cyan-500/10 text-cyan-400 font-semibold'
                  : 'text-slate-300 hover:text-white hover:bg-slate-800/60'
              }`
            }
          >
            Om os
          </NavLink>

          <NavLink
            to="/konceptet"
            className={({ isActive }) =>
              `px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-cyan-500/10 text-cyan-400 font-semibold'
                  : 'text-slate-300 hover:text-white hover:bg-slate-800/60'
              }`
            }
          >
            Konceptet
          </NavLink>

          {/* Destinationer Dropdown */}
          <div
            className="relative group"
            onMouseEnter={() => setDestDropdownOpen(true)}
            onMouseLeave={() => setDestDropdownOpen(false)}
          >
            <NavLink
              to="/destinationer"
              className={({ isActive }) =>
                `px-3.5 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${
                  isActive
                    ? 'bg-cyan-500/10 text-cyan-400 font-semibold'
                    : 'text-slate-300 hover:text-white hover:bg-slate-800/60'
                }`
              }
            >
              <span>Destinationer</span>
              <i className="fa-solid fa-chevron-down text-xs opacity-70 group-hover:rotate-180 transition-transform" aria-hidden="true" />
            </NavLink>

            {/* Dropdown Menu */}
            <div
              className={`absolute top-full left-0 w-72 pt-2 transition-all duration-200 ${
                destDropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
              }`}
            >
              <div className="bg-slate-900 rounded-xl border border-slate-800 shadow-2xl p-2 space-y-1">
                <NavLink
                  to="/destinationer"
                  end
                  className={({ isActive }) =>
                    `block px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider border-b border-slate-800/80 mb-1 transition-colors ${
                      isActive
                        ? 'bg-cyan-500/20 text-cyan-300 font-bold border-l-2 border-l-cyan-400 pl-2.5'
                        : 'text-cyan-400 hover:bg-cyan-950/40 hover:text-cyan-300'
                    }`
                  }
                >
                  Se alle destinationer &rarr;
                </NavLink>
                {destinationsList.map((dest) => (
                  <NavLink
                    key={dest.slug}
                    to={`/destinationer/${dest.slug}`}
                    className={({ isActive }) =>
                      `flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all ${
                        isActive
                          ? 'bg-cyan-500/20 text-cyan-300 font-semibold border-l-2 border-cyan-400 pl-2.5 shadow-sm'
                          : 'text-slate-300 hover:text-white hover:bg-slate-800/80 hover:translate-x-0.5'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span className="flex items-center gap-2">
                          <i
                            className={`fa-solid fa-location-dot text-xs ${
                              isActive ? 'text-cyan-400' : 'text-slate-500'
                            }`}
                            aria-hidden="true"
                          />
                          <span>{dest.name}</span>
                        </span>
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
                            isActive
                              ? 'bg-cyan-950 text-cyan-300 border border-cyan-800/80'
                              : 'bg-slate-800 text-slate-400'
                          }`}
                        >
                          {dest.land}
                        </span>
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          </div>

          <NavLink
            to="/processen"
            className={({ isActive }) =>
              `px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-cyan-500/10 text-cyan-400 font-semibold'
                  : 'text-slate-300 hover:text-white hover:bg-slate-800/60'
              }`
            }
          >
            Processen
          </NavLink>

          <NavLink
            to="/kontakt"
            className={({ isActive }) =>
              `ml-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all shadow-md ${
                isActive
                  ? 'bg-cyan-400 text-slate-950 shadow-cyan-400/20'
                  : 'bg-cyan-500 text-slate-950 hover:bg-cyan-400 hover:shadow-cyan-400/25'
              }`
            }
          >
            Kontakt os
          </NavLink>
        </nav>

        {/* Mobile Burger Menu Button (visible under 1200px) */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="min-[1200px]:hidden p-2.5 rounded-xl bg-slate-900 border border-slate-800 text-slate-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
          aria-label="Åbn menu"
        >
          <i className={`fa-solid ${mobileOpen ? 'fa-xmark' : 'fa-bars'} text-xl`} aria-hidden="true" />
        </button>
      </div>

      {/* Mobile Menu Panel (< 1200px) */}
      {mobileOpen && (
        <div className="min-[1200px]:hidden bg-slate-950 border-b border-slate-800 px-4 pt-3 pb-6 space-y-2 animate-in slide-in-from-top-4 duration-200">
          <NavLink
            to="/"
            end
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `block px-3 py-2.5 rounded-lg text-base font-medium ${
                isActive ? 'bg-cyan-500/20 text-cyan-300 font-bold' : 'text-slate-200 hover:bg-slate-900'
              }`
            }
          >
            Forside
          </NavLink>

          <NavLink
            to="/om-os"
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `block px-3 py-2.5 rounded-lg text-base font-medium ${
                isActive ? 'bg-cyan-500/20 text-cyan-300 font-bold' : 'text-slate-200 hover:bg-slate-900'
              }`
            }
          >
            Om os
          </NavLink>

          <NavLink
            to="/konceptet"
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `block px-3 py-2.5 rounded-lg text-base font-medium ${
                isActive ? 'bg-cyan-500/20 text-cyan-300 font-bold' : 'text-slate-200 hover:bg-slate-900'
              }`
            }
          >
            Konceptet
          </NavLink>

          <NavLink
            to="/destinationer"
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `block px-3 py-2.5 rounded-lg text-base font-medium ${
                isActive ? 'bg-cyan-500/20 text-cyan-300 font-bold' : 'text-slate-200 hover:bg-slate-900'
              }`
            }
          >
            Destinationer
          </NavLink>

          <NavLink
            to="/processen"
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `block px-3 py-2.5 rounded-lg text-base font-medium ${
                isActive ? 'bg-cyan-500/20 text-cyan-300 font-bold' : 'text-slate-200 hover:bg-slate-900'
              }`
            }
          >
            Processen
          </NavLink>

          <NavLink
            to="/kontakt"
            onClick={() => setMobileOpen(false)}
            className="block w-full text-center mt-3 px-4 py-3 rounded-xl bg-cyan-400 text-slate-950 font-bold shadow-lg shadow-cyan-400/20"
          >
            Kontakt os
          </NavLink>
        </div>
      )}
    </header>
  )
}
